import { createEffectiveSpotMap } from '../calculations/convertNotionalToBRL.js'
import { calculateTotalVolumeBRL } from '../calculations/calculateTotalVolumeBRL.js'
import { calculateNegotiatedVolumeBRL, countNegotiated, calculateNegotiatedTicketBRL } from '../calculations/calculateNegotiatedVolumeBRL.js'
import { calculateConvertedVolumeBRL, countConverted, calculateConvertedTicketBRL } from '../calculations/calculateConvertedVolumeBRL.js'
import { calculateConversionRate } from '../calculations/calculateConversionRate.js'
import { calculateAverageSpreadPips } from '../calculations/calculateAverageSpreadPips.js'
import { calculateRevenueBRL } from '../calculations/calculateRevenueBRL.js'
import { variationPct } from '@/utils/flowSightPeriods'

// Keys match the ids of the KPI slots in Overview.vue's template.
export const KPI_KEYS = [
  'totalVol', 'negotiatedVol', 'convertedVol', 'revenue', 'spread', 'conversionRate',
  'negotiatedTicket', 'convertedTicket', 'negotiatedCount', 'convertedCount',
]

// Os mesmos numeros que os NumberDisplay mostram, sobre um conjunto arbitrario
// de linhas. Reusa os modulos de calculo que os KPIs ja usam, entao o benchmark
// nao pode divergir do valor contra o qual e comparado.
//
// KPI novo tem que entrar em DOIS lugares: aqui e em KPI_KEYS. buildKpiVariations
// itera sobre KPI_KEYS - so medir nao basta, a variacao nunca chega na tela.
export function measureKpis (rows, effectiveSpotByRow) {
  return {
    totalVol: calculateTotalVolumeBRL(rows, effectiveSpotByRow),
    negotiatedVol: calculateNegotiatedVolumeBRL(rows, effectiveSpotByRow),
    convertedVol: calculateConvertedVolumeBRL(rows, effectiveSpotByRow),
    revenue: calculateRevenueBRL(rows, effectiveSpotByRow),
    spread: calculateAverageSpreadPips(rows),
    conversionRate: calculateConversionRate(rows, effectiveSpotByRow),
    negotiatedTicket: calculateNegotiatedTicketBRL(rows, effectiveSpotByRow),
    convertedTicket: calculateConvertedTicketBRL(rows, effectiveSpotByRow),
    negotiatedCount: countNegotiated(rows),
    convertedCount: countConverted(rows),
  }
}

// Measures the benchmark window through CROSSFILTER rather than by filtering
// the raw rows: the date dimension is swapped to the benchmark window, read,
// and put back. That way every OTHER active filter - side, counterparty, tenor,
// and any chart drilldown the user clicked - applies identically to both sides
// of the comparison. Rebuilding that set of filters by hand would drift the
// moment someone adds a new one.
//
// Safe to call synchronously: writing to a crossfilter dimension does not
// trigger a dc redraw on its own, and the current window is restored before
// this returns, so nothing on screen ever sees the benchmark state.
export function measureBenchmark ({ ndx, dimRfqDate, currentWindow, benchmarkWindow: window, effectiveSpotByRow }) {
  if (!ndx || !dimRfqDate || !window) return null

  const applyWindow = (w) => {
    dimRfqDate.filterAll()
    if (w) dimRfqDate.filterFunction((d) => d >= w.from && d <= w.to)
  }

  try {
    applyWindow(window)
    const rows = ndx.allFiltered()
    // No rows in the comparison window - the caller renders "—" instead of
    // treating an absent baseline as zero and claiming +100%.
    if (!rows.length) return null
    return measureKpis(rows, effectiveSpotByRow)
  } finally {
    applyWindow(currentWindow)
  }
}

// { totalVol: { current, baseline, pct, hasBaseline }, ... }
//
// `hasBaseline` is what separates "nothing to compare against" (no rows in the
// window - neutral, "Sem base") from "compared against a measured zero", where
// the percentage is undefined but the direction is real and still colours the
// KPI. Without that flag the two collapse into the same null and a day that
// went from 0% to 2% conversion would read as having no baseline at all.
export function buildKpiVariations (current, baseline) {
  const out = {}
  for (const key of KPI_KEYS) {
    const currentValue = current ? current[key] : null
    const baselineValue = baseline ? baseline[key] : null
    out[key] = {
      current: currentValue,
      baseline: baselineValue,
      pct: baseline ? variationPct(currentValue, baselineValue) : null,
      hasBaseline: !!baseline,
    }
  }
  return out
}

export { createEffectiveSpotMap }
